import React, { useLayoutEffect, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { formatMeasure, snapChipText } from '../../lib/objectSnap';

/**
 * While an object is moved or resized this overlay — rendered inside the page — shows its exact geometry in the document
 * unit, to the thousandth: left / top (X, Y), width / height, the distance from the right and bottom page edges, and
 * what each axis snapped to ("Left on margin", "Centre on page centre", "Right on text box edge"). The values being
 * changed (position while moving, size while resizing) are emphasised.
 *
 * Driven by state.objectDrag = { id, mode: 'move' | 'resize', x, y, width, height, snaps: { x, y }, count }
 * (SET_OBJECT_DRAG from CanvasObject, cleared on mouse up).
 */
export const ObjectDragOverlay = ({ zoom, pageWidth, pageHeight }) => {
  const { state } = useEditor();
  const drag = state.objectDrag;
  // the readout's real size (it widens with the unit and the chips) so it can be kept on the page
  const tipRef = useRef(null);
  const [tipSize, setTipSize] = useState({ w: 190, h: 64 });
  // measured after every render (the chips change the width mid-drag); the equality guard stops a re-render loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el) return;
    const w = Math.ceil(el.offsetWidth), h = Math.ceil(el.offsetHeight);
    if (w && h && (w !== tipSize.w || h !== tipSize.h)) setTipSize({ w, h });
  });
  if (!drag || !Number.isFinite(drag.x) || !Number.isFinite(drag.y)) return null;
  const unit = state.canvasUnit || 'in';
  const { x, y, width, height } = drag;
  const resizing = drag.mode === 'resize';
  const fromRight = pageWidth - (x + width), fromBottom = pageHeight - (y + height);
  const chips = [drag.snaps && drag.snaps.x, drag.snaps && drag.snaps.y].filter(Boolean);
  const more = (drag.count || 1) - 1;
  // below the object's left edge; above it when there is no room, inside it when there is none either — and always
  // within the page (or the object itself when it hangs over the edge / sits in the scratch area)
  const TIP_W = tipSize.w, TIP_H = tipSize.h, GAP = 8;
  const ox = x * zoom, oy = y * zoom, ow = width * zoom, oh = height * zoom;
  const minL = Math.min(0, ox), maxR = Math.max(pageWidth * zoom, ox + ow);
  const minT = Math.min(0, oy), maxB = Math.max(pageHeight * zoom, oy + oh);
  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
  let top = oy + oh + GAP;
  if (top + TIP_H > maxB - 4) top = oy - TIP_H - GAP;
  if (top < minT + 4) top = oy + GAP;
  const left = clamp(ox, minL + 4, maxR - TIP_W - 4);
  top = clamp(top, minT + 4, maxB - TIP_H - 4);
  const strong = 'text-[12px] font-semibold text-foreground tabular-nums';
  const soft = 'text-[12px] text-foreground/80 tabular-nums';
  const label = 'text-[10px] uppercase tracking-wide text-muted-foreground self-center';
  return (
    <div
      className="absolute pointer-events-none rounded-md border border-border/60 bg-popover/95 backdrop-blur-md shadow-lg px-2 py-1 text-foreground whitespace-nowrap select-none"
      ref={tipRef}
      style={{ zIndex: 1002, left, top }}
      role="status"
      aria-live="polite"
      data-testid="object-drag-tip"
      data-mode={drag.mode}
      data-object-id={drag.id}
    >
      <div className="grid grid-cols-[auto_auto_auto_auto] gap-x-2 gap-y-0.5 items-baseline">
        <span className={label}>X</span><span className={resizing ? soft : strong} data-testid="object-drag-tip-x">{formatMeasure(x, unit)}</span>
        <span className={label}>Y</span><span className={resizing ? soft : strong} data-testid="object-drag-tip-y">{formatMeasure(y, unit)}</span>
        <span className={label}>W</span><span className={resizing ? strong : soft} data-testid="object-drag-tip-w">{formatMeasure(width, unit)}</span>
        <span className={label}>H</span><span className={resizing ? strong : soft} data-testid="object-drag-tip-h">{formatMeasure(height, unit)}</span>
        <span className={`${label} normal-case tracking-normal`}>from right</span><span className="text-[11px] text-muted-foreground tabular-nums" data-testid="object-drag-tip-right">{formatMeasure(fromRight, unit)}</span>
        <span className={`${label} normal-case tracking-normal`}>from bottom</span><span className="text-[11px] text-muted-foreground tabular-nums" data-testid="object-drag-tip-bottom">{formatMeasure(fromBottom, unit)}</span>
      </div>
      {(chips.length > 0 || more > 0) && (
        <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
          {chips.map((s) => (
            <span key={s.axis} className="text-accent-foreground bg-accent/80 rounded px-1 leading-4" data-testid={`object-drag-tip-snap-${s.axis}`} data-kind={s.kind}>{snapChipText(s)}</span>
          ))}
          {more > 0 && <span className="text-muted-foreground" data-testid="object-drag-tip-more">+{more} more move together</span>}
        </div>
      )}
    </div>
  );
};
